export const en: Record<string, string> = {
  // ── Shared ────────────────────────────────────────────────────────────────
  "shared.appName": "SkillBridge",
  "shared.footer.copyright": "© {{year}} SkillBridge. All rights reserved.",
  "shared.footer.version": "Version {{version}}",
  "shared.save": "Save",
  "shared.cancel": "Cancel",
  "shared.close": "Close",
  "shared.edit": "Edit",
  "shared.delete": "Delete",
  "shared.remove": "Remove",
  "shared.back": "Back",
  "shared.view": "View",
  "shared.manage": "Manage",
  "shared.create": "Create",
  "shared.add": "Add",
  "shared.approve": "Approve",
  "shared.reject": "Reject",
  "shared.publish": "Publish",
  "shared.withdraw": "Withdraw",
  "shared.invite": "Invite",
  "shared.search": "Search",
  "shared.saveChanges": "Save Changes",
  "shared.name": "Name",
  "shared.email": "Email",
  "shared.description": "Description",
  "shared.status": "Status",
  "shared.type": "Type",
  "shared.role": "Role",
  "shared.actions": "Actions",
  "shared.members": "Members",
  "shared.location": "Location",
  "shared.county": "County",
  "shared.city": "City",
  "shared.website": "Website",
  "shared.date": "Date",
  "shared.startDate": "Start Date",
  "shared.endDate": "End Date",
  "shared.deadline": "Deadline",
  "shared.unlimited": "Unlimited",
  "shared.error": "Error",
  "shared.selectCounty": "Select county",
  "shared.selectCity": "Select or type city",
  "shared.cityHint": "Can't find your city? Just type it in.",
  "shared.verified": "Verified",
  "shared.incomplete": "Incomplete",

  // ── Navigation ────────────────────────────────────────────────────────────
  "nav.dashboard": "Dashboard",
  "nav.events": "Events",
  "nav.myEvents": "My Events",
  "nav.community": "Community",
  "nav.checkIn": "Check In",
  "nav.organizations": "Organizations",
  "nav.newOrganization": "New organization",
  "nav.profileSettings": "Profile settings",
  "nav.userGuide": "User guide",
  "nav.logout": "Logout",

  // ── Layout ────────────────────────────────────────────────────────────────
  "layout.profileIncomplete":
    "Your profile is incomplete. Please complete your profile to access all features.",
  "layout.completeProfile": "Complete Profile",
  "layout.phoneNumberAdded":
    "We've added a phone number field to volunteer profiles. Please add yours to keep your profile complete.",
  "layout.addPhoneNumber": "Add Phone Number",
  "layout.prerelease":
    "This is a prerelease version of SkillBridge. Features and data may change without notice.",

  // ── i18n ─────────────────────────────────────────────────────────────────
  "i18n.switchLanguage": "Switch language",

  // ── Auth ──────────────────────────────────────────────────────────────────
  "auth.signIn.title": "Welcome Back",
  "auth.signIn.subtitle": "Sign in to access your account",
  "auth.signIn.submit": "Sign In",
  "auth.signIn.submitting": "Signing in...",
  "auth.signIn.error": "An error occurred during sign in",
  "auth.signIn.noAccount": "Don't have an account?",
  "auth.signIn.createOne": "Create one",
  "auth.signUp.title": "Create an Account",
  "auth.signUp.subtitle": "Join our platform and start making a difference",
  "auth.signUp.submit": "Sign Up",
  "auth.signUp.error": "An error occurred. Please try again.",
  "auth.signUp.hasAccount": "Already have an account?",
  "auth.signUp.signIn": "Sign In",
  "auth.signUp.confirmPassword": "Confirm Password",
  "auth.signUp.confirmPasswordPlaceholder": "Confirm your password",
  "auth.signUp.confirmPasswordRequired": "Please confirm your password",
  "auth.signUp.acceptTermsPrefix": "I agree to the",
  "auth.signUp.termsLink": "Terms and Conditions",
  "auth.signUp.acceptTermsAnd": "and",
  "auth.signUp.privacyLink": "Privacy Policy",
  "auth.logout.title": "Logging Out",
  "auth.logout.message": "Please wait while we clean up...",
  "auth.logout.step1": "Invalidating server session",
  "auth.logout.step2": "Clearing local state",
  "auth.logout.step3": "Finalizing logout",
  "auth.field.emailPlaceholder": "Enter your email",
  "auth.field.emailRequired": "Please enter a valid email",
  "auth.field.passwordPlaceholder": "Enter your password",
  "auth.field.passwordRequired": "Please enter your password",
  "auth.signUp.passwordTooShort": "Password must be at least 12 characters",
  "auth.field.namePlaceholder": "Enter your name",
  "auth.field.nameRequired": "Please enter your name",
  "auth.field.emailLabel": "Email",
  "auth.field.passwordLabel": "Password",
  "auth.field.nameLabel": "Name",
  "auth.field.phonePlaceholder": "Enter your phone number",
  "auth.field.phoneRequired": "Please enter your phone number",
  "auth.field.phoneLabel": "Phone number",

  // ── Welcome ───────────────────────────────────────────────────────────────
  "welcome.title": "Welcome to SkillBridge",
  "welcome.subtitle":
    "Connecting talented volunteers with organizations that need their expertise—making skills meaningful.",
  "welcome.getStarted": "Get Started",
  "welcome.signIn": "Sign In",
  "welcome.whyUs": "Why Choose SkillBridge?",
  "welcome.feature1.title": "Connect Skills with Impact",
  "welcome.feature1.description":
    "Bridge the gap between talented volunteers and organizations that need their expertise for meaningful projects.",
  "welcome.feature2.title": "Find Your Perfect Match",
  "welcome.feature2.description":
    "Our intelligent matching algorithm connects you with opportunities that align with your skills and interests.",
  "welcome.feature3.title": "Grow and Contribute",
  "welcome.feature3.description":
    "Track your progress, develop new skills, and make a tangible difference in your community.",
  "welcome.feature4.title": "Build Community",
  "welcome.feature4.description":
    "Join a vibrant community of volunteers and organizations working together to create positive change.",
  "welcome.howItWorks": "How It Works",
  "welcome.step1.title": "Create Your Profile",
  "welcome.step1.description": "List your skills, availability, and interests to get started.",
  "welcome.step2.title": "Get Matched",
  "welcome.step2.description": "Discover opportunities that fit your skills and schedule.",
  "welcome.step3.title": "Make an Impact",
  "welcome.step3.description": "Contribute your skills and track your volunteer journey.",
  "welcome.cta.title": "Ready to Make a Difference?",
  "welcome.cta.subtitle":
    "Join thousands of volunteers who are already using SkillBridge to create meaningful impact.",
  "welcome.cta.joinVolunteer": "Join as a Volunteer",

  // ── Dashboard ─────────────────────────────────────────────────────────────
  dashboard: "Dashboard",
  "dashboard.welcome": "Welcome to SkillBridge",
  "dashboard.registerOrg": "Register an Organization",
  "dashboard.registerOrgDesc": "Create and manage a volunteering organization",
  "dashboard.myEvents": "My Events",
  "dashboard.myEventsDesc": "View all your registered events",
  "dashboard.feedback": "Send Feedback",
  "dashboard.feedbackDesc": "Share your thoughts or report an issue",
  "dashboard.registeredEvents": "Registered Events",
  "dashboard.noRegisteredEvents": "No registered events yet.",
  "dashboard.upcomingShifts": "Upcoming Shifts",
  "dashboard.noUpcomingShifts": "No upcoming shifts assigned to you yet.",
  "dashboard.showMore": "Show more",
  "dashboard.recommendedEvents": "Recommended for You",
  "dashboard.noRecommendedEvents": "Could not find any event matching your skills.",
  "dashboard.findOtherEvents": "Find some others",
  "dashboard.matchScore": "match",
  "dashboard.noMatch": "No skill match",

  // ── Events ────────────────────────────────────────────────────────────────
  "events.status.draft": "Draft",
  "events.status.published": "Published",
  "events.status.in_progress": "In Progress",
  "events.status.completed": "Completed",
  "events.status.cancelled": "Cancelled",

  "events.registrationStatus.pending": "Pending",
  "events.registrationStatus.approved": "Approved",
  "events.registrationStatus.rejected": "Rejected",
  "events.registrationStatus.waitlisted": "Waitlisted",
  "events.registrationStatus.withdrew": "Withdrew",
  "events.registrationStatus.attended": "Attended",
  "events.registrationStatus.no_show": "No Show",
  "events.registrationStatus.pending_parental_consent": "Pending Parental Consent",

  "events.list.title": "Events",
  "events.list.search": "Search events...",
  "events.list.filterAll": "All",
  "events.list.filterUpcoming": "Upcoming",
  "events.list.filterOngoing": "Ongoing",
  "events.list.filterPast": "Past",
  "events.list.allCounties": "All counties",
  "events.list.empty": "No upcoming events at the moment. Check back soon!",
  "events.list.viewEvent": "View Event",
  "events.list.deadline": "Deadline:",

  // ── Community ────────────────────────────────────────────────────────────
  "community.list.title": "Community",
  "community.list.search": "Search volunteers...",
  "community.list.empty": "No volunteers to show yet. Check back soon!",

  "events.detail.volunteers": "volunteers",
  "events.detail.deadline": "Deadline:",
  "events.detail.withdrawConfirm": "Withdraw from this event?",
  "events.detail.unregister": "Unregister",
  "events.detail.register": "Register as Volunteer",
  "events.detail.registerDisabledMinAge":
    "You must be at least 15 years old to register for events.",
  "events.detail.viewVolunteers": "View Volunteers",
  "events.detail.skills": "Good to Have Skills",
  "events.detail.registeredVolunteers": "Registered Volunteers",
  "events.detail.teams": "Teams",
  "events.detail.teamVolunteers": "Volunteers:",

  "events.create.title": "Create Event",
  "events.create.titleLabel": "Title",
  "events.create.titlePlaceholder": "Event title",
  "events.create.coverUrl": "Cover Image URL",
  "events.create.coverPlaceholder": "https://example.com/image.jpg",
  "events.create.descriptionPlaceholder": "What will volunteers be doing?",
  "events.create.locationPlaceholder": "Street, venue or landmark",
  "events.create.fullDay": "Full day (no specific start/end time)",
  "events.create.deadlineLabel": "Registration Deadline",
  "events.create.deadlineInPastError": "Registration deadline cannot be in the past.",
  "events.create.deadlineError": "Registration deadline must be on or before the start date.",
  "events.create.maxVolunteers": "Max Volunteers",
  "events.create.skills": "Good to Have Skills",
  "events.create.skillsPlaceholder": "Select recommended skills (optional)",
  "events.create.startDateInPastError": "Start date cannot be in the past.",
  "events.create.endDateError": "End date must be on or after start date",
  "events.create.submit": "Create Event",
  "events.create.submitAndPublish": "Create and Publish",

  "events.manage.backToEvent": "Back to Event",
  "events.manage.editDetails": "Edit Details",
  "events.manage.publishConfirm": "Publish this event?",
  "events.manage.cancelConfirm": "Cancel this event?",
  "events.manage.close": "Complete Event",
  "events.manage.closeConfirm":
    "Mark this event as completed? All open timesheet sessions will be force-closed.",
  "events.manage.startDate": "Start date",
  "events.manage.endDate": "End date",
  "events.manage.deadline": "Reg. deadline",
  "events.manage.registered": "Registered",
  "events.manage.maxVolunteers": "Max volunteers",
  "events.manage.registrations": "Registrations",
  "events.manage.teams": "Teams",
  "events.manage.timesheet": "Timesheet",
  "events.manage.shifts": "Shifts",

  "events.edit.features": "Features",
  "events.edit.eventDocumentsEnabled": "Event documents & tracking",
  "events.edit.eventDocumentsEnabledHint":
    "Allow volunteers to download contracts, the job description annex, certificates and evaluation sheets, and let them log hours manually.",
  "events.edit.qrTrackingEnabled": "QR check-in",
  "events.edit.qrTrackingEnabledHint":
    "Let volunteers check in and out via QR code, in addition to manual hour logging. Requires event documents to be enabled.",
  "events.edit.shiftsEnabled": "Shifts",
  "events.edit.shiftsEnabledHint":
    "Break the event into shifts and assign approved volunteers to them, manually or automatically.",

  "events.timesheet.qrCardTitle": "Check-in QR code",
  "events.timesheet.wholeEvent": "Whole event",
  "events.timesheet.refreshQr": "Refresh",
  "events.timesheet.refreshesIn": "Refreshes in",
  "events.timesheet.volunteer": "Volunteer",
  "events.timesheet.team": "Team",
  "events.timesheet.checkIn": "Check-in",
  "events.timesheet.checkOut": "Check-out",
  "events.timesheet.duration": "Duration",
  "events.timesheet.live": "Live",
  "events.timesheet.forceOut": "Force out",
  "events.timesheet.forceOutConfirm": "Check this volunteer out now?",
  "events.timesheet.editEntry": "Edit timesheet entry",

  "events.checkIn.title": "Check in / out",
  "events.checkIn.hint": "Point your camera at the event's QR code to check in or out.",
  "events.checkIn.checkedIn": "You're checked in!",
  "events.checkIn.checkedOut": "You're checked out.",
  "events.checkIn.duration": "Time logged:",
  "events.checkIn.scanAgain": "Scan again",
  "events.checkIn.cameraError":
    "Couldn't access the camera. Check your browser permissions and try again.",
  "events.checkIn.invalidQr": "That doesn't look like a valid check-in code.",
  "events.checkIn.scanError": "Couldn't process the scan. Please try again.",

  "events.registrations.title": "Registrations",
  "events.registrations.volunteer": "Volunteer",
  "events.registrations.team": "Team",
  "events.registrations.match": "Match",
  "events.registrations.status": "Status",
  "events.registrations.applied": "Applied",
  "events.registrations.assignTeam": "Assign team",
  "events.registrations.assignTeamDisabled":
    "The registration must be approved before assigning a team",
  "events.registrations.shifts": "Shifts",
  "events.registrations.assignShift": "Assign shifts",
  "events.registrations.assignShiftDisabled":
    "The registration must be approved before assigning a shift",
  "events.registrations.shiftModalTitle": "Assign shifts to {{name}}",
  "events.registrations.waitlist": "Waitlist",
  "events.registrations.downloadEvaluation": "Download Evaluation",
  "events.registrations.evaluate": "Evaluate",
  "events.registrations.continueEvaluation": "Continue Evaluation",
  "events.registrations.viewEvaluation": "View Evaluation",
  "events.registrations.evaluationRequired":
    "Complete the evaluation before downloading the evaluation document",
  "events.registrations.evaluationModalTitle": "Evaluate Volunteer",
  "events.registrations.evaluationLocked":
    "This evaluation has been completed and can no longer be changed.",
  "events.registrations.evaluationRating": "Rating",
  "events.registrations.evaluationRatingPlaceholder": "Select a rating",
  "events.registrations.evaluationComment": "Comment (optional)",
  "events.registrations.evaluationCommentPlaceholder":
    "Add any additional notes about the volunteer's performance",
  "events.registrations.saveEvaluationDraft": "Save",
  "events.registrations.completeEvaluation": "Complete Evaluation",
  "events.registrations.verifyConsent": "Verify Physical Consent",
  "events.registrations.consentVerifyConfirm":
    "Confirm that you have physically received the signed parental consent form from this volunteer.",
  "events.registrations.consentVerified": "Parental consent verified. Registration approved.",
  "events.registrations.consentVerifyError": "Could not verify parental consent. Please try again.",

  "events.evaluationRating.outstanding": "Outstanding",
  "events.evaluationRating.meets_expectations": "Meets Expectations",
  "events.evaluationRating.needs_improvement": "Needs Improvement",
  "events.evaluationRating.unsatisfactory": "Unsatisfactory",

  "events.evaluationStatus.not_started": "Not Started",
  "events.evaluationStatus.in_progress": "In Progress",
  "events.evaluationStatus.done": "Done",

  "events.teams.addTeam": "Add Team",
  "events.teams.addTeamDisabled": "Teams can't be added to a cancelled, completed, or ended event",
  "events.teams.description": "Description",
  "events.teams.maxVolunteers": "Max Volunteers",
  "events.teams.addMember": "Add Member",
  "events.teams.deleteConfirm": "Delete this team?",
  "events.teams.removeConfirm": "Remove from team?",
  "events.teams.noMembers": "No members assigned yet.",
  "events.teams.createTeam": "Create Team",
  "events.teams.teamName": "Team Name",
  "events.teams.addMemberTitle": "Add Member to Team",
  "events.teams.selectVolunteer": "Select a volunteer",
  "events.teams.noEligible": "No eligible registrations to add.",

  "events.shifts.addShift": "Add Shift",
  "events.shifts.addShiftDisabled":
    "Shifts can't be added to a cancelled, completed, or ended event",
  "events.shifts.startTime": "Start time",
  "events.shifts.endTime": "End time",
  "events.shifts.maxVolunteers": "Max Volunteers",
  "events.shifts.assigned": "Assigned",
  "events.shifts.untitled": "Untitled shift",
  "events.shifts.team": "Team",
  "events.shifts.allTeams": "All teams",
  "events.shifts.selectTeam": "Select a team",
  "events.shifts.teamRequired": "This event has teams — select the team this shift belongs to.",
  "events.shifts.selectDays": "Which day(s) is this shift for?",
  "events.shifts.dayLabel": "Day {{index}} ({{date}})",
  "events.shifts.selectDaysRequired": "Select at least one day for this shift.",
  "events.shifts.noShifts": "No shifts created yet.",
  "events.shifts.createShift": "Create Shift",
  "events.shifts.editShift": "Edit Shift",
  "events.shifts.deleteConfirm": "Delete this shift? Any volunteer assignments will be removed.",
  "events.shifts.autoDistribute": "Auto-distribute",
  "events.shifts.autoDistributeConfirm":
    "Automatically assign approved volunteers to shifts based on availability and skills?",

  "events.myRegistrations.title": "My Events",
  "events.myRegistrations.upcoming": "Upcoming",
  "events.myRegistrations.past": "Past",
  "events.myRegistrations.emptyPast": "You haven't attended any events yet.",
  "events.myRegistrations.event": "Event",
  "events.myRegistrations.organization": "Organization",
  "events.myRegistrations.withdrawConfirm": "Withdraw from this event?",
  "events.myRegistrations.downloadContract": "Download Contract",
  "events.myRegistrations.contractError": "Could not download the contract. Please try again.",
  "events.myRegistrations.contractPrefillPrompt":
    "Would you like to prefill your personal details? They won't be saved.",
  "events.myRegistrations.contractPrefillYes": "Yes, fill in details",
  "events.myRegistrations.contractPrefillNo": "No, download as-is",
  "events.myRegistrations.contractPrefillModalTitle": "Personal details for contract",
  "events.myRegistrations.contractPrefillNote":
    "All fields are optional. This information will appear in the PDF only and will not be stored.",
  "events.myRegistrations.contractPrefillLocality": "Locality",
  "events.myRegistrations.contractPrefillCounty": "County",
  "events.myRegistrations.contractPrefillIdSeries": "ID Card Series",
  "events.myRegistrations.contractPrefillIdNumber": "ID Card Number",
  "events.myRegistrations.contractPrefillCnp": "CNP",
  "events.myRegistrations.contractPrefillDownload": "Download",
  "events.myRegistrations.downloadJobDescription": "Download Job Description",
  "events.myRegistrations.jobDescriptionError":
    "Could not download the job description. Please try again.",
  "events.myRegistrations.downloadCertificate": "Download Certificate",
  "events.myRegistrations.certificateError":
    "Could not download the certificate. Please try again.",
  "events.myRegistrations.downloadEvaluation": "Download Evaluation Sheet",
  "events.myRegistrations.evaluationError":
    "Could not download the evaluation sheet. Please try again.",
  "events.myRegistrations.logHours": "Log Hours",
  "events.myRegistrations.logHoursTitle": "Log Volunteer Hours",
  "events.myRegistrations.logHoursCheckIn": "Check-in",
  "events.myRegistrations.logHoursCheckOut": "Check-out",
  "events.myRegistrations.logHoursSuccess":
    "Hours logged successfully. An admin will review and validate them.",
  "events.myRegistrations.logHoursError": "Could not log hours. Please try again.",
  "events.myRegistrations.logHoursInvalidRange": "Check-out must be after check-in.",
  "events.myRegistrations.downloadParentalConsent": "Download Parental Consent Form",
  "events.myRegistrations.parentalConsentError":
    "Could not download the parental consent form. Please try again.",
  "events.myRegistrations.empty": "You haven't registered for any events yet.",
  "events.myRegistrations.browseEvents": "Browse events",

  // ── Organizations ─────────────────────────────────────────────────────────
  "organizations.create.title": "Create Organization",
  "organizations.create.adminNote": "You will be assigned as Organization Admin",
  "organizations.create.adminDesc":
    "As the creator of this organization, you will automatically receive the Admin role, giving you full control over members, events, and settings.",
  "organizations.create.namePlaceholder": "Organization name",
  "organizations.create.descriptionPlaceholder": "What does your organization do?",
  "organizations.create.taxId": "Tax ID (CUI)",
  "organizations.create.taxIdPlaceholder": "e.g. 12345678",
  "organizations.create.contactEmail": "Contact Email",
  "organizations.create.contactEmailPlaceholder": "contact@org.ro",
  "organizations.create.websitePlaceholder": "https://...",
  "organizations.create.submit": "Create Organization",

  "organizations.detail.email": "Email",
  "organizations.detail.website": "Website",
  "organizations.detail.phone": "Phone",
  "organizations.detail.taxId": "CUI",
  "organizations.detail.events": "Events",
  "organizations.detail.noEvents": "No events yet.",

  "organizations.manage.backToOrg": "Back to Organization",
  "organizations.manage.title": "Manage Organization",
  "organizations.manage.settings": "Settings",
  "organizations.manage.membersDesc": "Invite, manage roles and remove members",
  "organizations.manage.settingsDesc": "Update organization details and information",
  "organizations.manage.noEvents": "No events yet. Create the first one!",
  "organizations.manage.ongoingEvents": "Ongoing Events",
  "organizations.manage.completedEvents": "Completed Events",
  "organizations.manage.noOngoingEvents": "No active events. Create the first one!",
  "organizations.manage.noCompletedEvents": "No completed events yet.",
  "organizations.manage.createEvent": "Create Event",
  "organizations.manage.createEventDesc": "Set up a new event for volunteers to register",
  "organizations.manage.contract": "Contract Template",
  "organizations.manage.contractDesc": "Preview and download the volunteer contract template",
  "organizations.manage.branding": "Legal Branding",
  "organizations.manage.brandingDesc": "Upload signature and stamp for official documents",
  "organizations.contract.description":
    "This is the volunteer contract template used for your organization. Download it to review the terms that volunteers will see when they generate their contract for an event.",
  "organizations.contract.download": "Download Template",
  "organizations.contract.loadError": "Failed to load contract template. Please try again.",
  "organizations.contract.downloadError": "Failed to download contract template. Please try again.",
  "organizations.branding.description":
    "Upload a transparent-background PNG signature and official stamp. These assets are automatically embedded in all generated PDFs (contracts, certificates, job descriptions, and evaluation sheets).",
  "organizations.branding.signature": "Signature",
  "organizations.branding.stamp": "Stamp",
  "organizations.branding.uploaded": "Uploaded",
  "organizations.branding.notUploaded": "Not uploaded",
  "organizations.branding.upload": "Upload",
  "organizations.branding.remove": "Remove",
  "organizations.branding.uploadSignatureHint":
    "Transparent PNG of the legal representative's signature.",
  "organizations.branding.uploadStampHint": "Transparent PNG of the organization's official stamp.",
  "organizations.branding.uploadError": "Upload failed. Please try again.",
  "organizations.branding.removeError": "Remove failed. Please try again.",

  "organizations.members.title": "Members",
  "organizations.members.invite": "Invite Member",
  "organizations.members.member": "Member",
  "organizations.members.joined": "Joined",
  "organizations.members.removeConfirm": "Remove this member?",
  "organizations.members.inviteTitle": "Invite Member",
  "organizations.members.invitePlaceholder": "member@example.com",
  "organizations.members.minor": "Minor",

  "organizations.settings.title": "Organization Settings",
  "organizations.settings.legalRepresentativeName": "Legal Representative",
  "organizations.settings.legalRepresentativeNameHint":
    "The person authorized to sign on behalf of the organization (used on volunteer contracts).",
  "organizations.settings.pickMyself": "Pick yourself",
  "organizations.settings.nationalRegisterNumber": "National Register Number",
  "organizations.settings.nationalRegisterNumberHint":
    "Entry ID from the Registrul Național al Persoanelor Juridice Fără Scop Patrimonial (e.g. 1234/A/2020). Printed in the document header.",
  "organizations.settings.nationalRegisterNumberPlaceholder": "e.g. 1234/A/2020",
  "organizations.types.ong.label": "NGO (Association / Foundation / Federation)",
  "organizations.types.ong.description": "Officially registered non-profit organizations.",
  "organizations.types.company.label": "Private Company (LLC / Corp / Sole Trader)",
  "organizations.types.company.description":
    "Businesses organizing corporate volunteering (CSR) or social events.",
  "organizations.types.public_institution.label": "Public / Educational Institution",
  "organizations.types.public_institution.description":
    "Schools, universities, museums, city halls, or other public authorities.",
  "organizations.types.informal_group.label": "Informal Group / Civic Initiative",
  "organizations.types.informal_group.description":
    "Groups of people without an official legal entity.",
  "organizations.types.other.label": "Other Type of Organization",
  "organizations.types.other.description": "Structures that do not fit the categories above.",

  "organizations.typeShort.ong": "ONG",
  "organizations.typeShort.company": "Company",
  "organizations.typeShort.public_institution": "Public Institution",
  "organizations.typeShort.informal_group": "Informal Group",
  "organizations.typeShort.other": "Other",

  "organizations.roles.admin": "Admin",
  "organizations.roles.coordinator": "Coordinator",
  "organizations.roles.member": "Member",

  // ── Profile ───────────────────────────────────────────────────────────────
  "profile.title": "Profile",
  "profile.skills": "Skills",
  "profile.security": "Security",
  "profile.privacy": "Privacy",

  "profile.completion.title": "Profile Completion",
  "profile.completion.subtitle": "Complete these steps to register for events.",
  "profile.completion.dateOfBirth": "Date of birth",
  "profile.completion.availability": "Weekly availability",

  "profile.details.title": "My Profile",
  "profile.details.subtitle":
    "Update your user details and select your availability for the volunteer actions.",
  "profile.details.uploadPicture": "Click to upload profile picture",
  "profile.details.upload": "Click to upload",
  "profile.details.personalDetails": "Personal Details",
  "profile.details.nameLabel": "Name",
  "profile.details.namePlaceholder": "Enter your name",
  "profile.details.nameRequired": "Please enter your name",
  "profile.details.phoneLabel": "Phone number",
  "profile.details.phonePlaceholder": "Enter your phone number",
  "profile.details.phoneRequired": "Please enter your phone number",
  "profile.details.emailLabel": "Email",
  "profile.details.emailPlaceholder": "Enter your email",
  "profile.details.emailNote": "Email updates are not supported yet.",
  "profile.details.dateOfBirthLabel": "Date of Birth",
  "profile.details.dateOfBirthPlaceholder": "Select your date of birth",
  "profile.details.dateOfBirthRequired": "Required to register for events",
  "profile.details.dateOfBirthLocked":
    "Date of birth cannot be changed after it has been set. Contact support if you need to update it.",
  "profile.details.imageTypeError": "Only JPEG/JPG and PNG images are allowed",
  "profile.details.imageSizeError": "Image size must be less than 5MB",
  "profile.details.minorNotice":
    "As a minor (under 18), events that require a volunteer agreement will place your registration in 'Pending Parental Consent' status. You will need to bring a signed parental consent form in person for a coordinator to verify on-site before your registration can be approved.",
  "profile.details.save": "Save Profile",

  "profile.security.title": "Security",
  "profile.security.subtitle": "Manage your account security settings.",

  "profile.password.title": "Change Password",
  "profile.password.current": "Current Password",
  "profile.password.currentPlaceholder": "Enter current password",
  "profile.password.new": "New Password",
  "profile.password.newPlaceholder": "Enter new password (min. 8 characters)",
  "profile.password.minLength": "Password must be at least 8 characters.",
  "profile.password.confirm": "Confirm New Password",
  "profile.password.confirmPlaceholder": "Confirm new password",
  "profile.password.mismatch": "Passwords do not match.",
  "profile.password.submit": "Update Password",
  "profile.password.success": "Password updated successfully!",
  "profile.password.error": "Failed to update password. Please try again.",

  "profile.availability.title": "Weekly Availability",
  "profile.availability.available": "Available",
  "profile.availability.startTime": "Start Time",
  "profile.availability.endTime": "End Time",
  "profile.availability.to": "to",
  "profile.availability.save": "Save Availability",
  "profile.availability.monday": "Monday",
  "profile.availability.tuesday": "Tuesday",
  "profile.availability.wednesday": "Wednesday",
  "profile.availability.thursday": "Thursday",
  "profile.availability.friday": "Friday",
  "profile.availability.saturday": "Saturday",
  "profile.availability.sunday": "Sunday",
  "profile.availability.notSet":
    "You haven't set your availability yet. Add at least one day so organisations know when you can volunteer.",

  "profile.skills.title": "My Skills",
  "profile.skills.subtitle": "Select the skills that best describe your volunteering abilities.",
  "profile.skills.propose": "Propose a New Skill",
  "profile.skills.proposeHint":
    "Can't find a skill that fits you? Suggest it and our team will review it.",
  "profile.skills.proposeNamePlaceholder": "e.g. Beekeeping, Sign Language...",
  "profile.skills.proposeDescriptionPlaceholder": "Briefly describe this skill (optional)",
  "profile.skills.proposeSubmit": "Submit Proposal",
  "profile.skills.save": "Save Skills",
  "profile.skills.new": "New",
  "profile.skills.searchPlaceholder": "Search skills...",
  "profile.skills.noResults": "No skills found matching your search.",
  "profile.skills.unsavedChanges": "You have unsaved changes",

  // ── Skill categories ─────────────────────────────────────────────────────
  "skills.category.Technical": "Technical",
  "skills.category.Creative": "Creative",
  "skills.category.Physical": "Physical",
  "skills.category.Social": "Social",

  "profile.privacy.title": "Privacy & Data",
  "profile.privacy.subtitle": "Manage the data SkillBridge holds about you.",
  "profile.privacy.exportTitle": "Request Your Data",
  "profile.privacy.exportDescription":
    "Download a copy of all the data we store about you, including your profile, skills, organisation memberships, event registrations, feedback and notifications.",
  "profile.privacy.exportSubmit": "Download My Data",
  "profile.privacy.exportError": "Failed to export your data. Please try again.",
  "profile.privacy.deleteTitle": "Delete Account",
  "profile.privacy.deleteDescription":
    "Permanently delete your account and all associated data, including any organisations you created. This action cannot be undone.",
  "profile.privacy.deleteSubmit": "Delete My Account",
  "profile.privacy.deleteConfirm": "Are you sure you want to permanently delete your account?",
  "profile.privacy.deleteSuccess": "Your account has been deleted.",
  "profile.privacy.deleteError": "Failed to delete your account. Please try again.",

  // ── Feedback ──────────────────────────────────────────────────────────────
  "feedback.title": "Send Feedback",
  "feedback.submit": "Submit",
  "feedback.category.label": "Category",
  "feedback.category.suggestion": "Suggestion",
  "feedback.category.bug": "Bug Report",
  "feedback.category.other": "Other",
  "feedback.message": "Message",
  "feedback.messagePlaceholder": "Tell us what's on your mind...",

  // ── Notifications ────────────────────────────────────────────────────────
  "notifications.title": "Notifications",
  "notifications.empty": "You have no notifications yet.",
  "notifications.markAllRead": "Mark all as read",
  "notifications.viewAll": "View all",
  "notifications.loadMore": "Load more",
  "notifications.deleteConfirm": "Delete this notification?",
  "notifications.message.registrationApproved":
    'Your registration for "{{eventTitle}}" was approved.',
  "notifications.message.registrationRejected":
    'Your registration for "{{eventTitle}}" was declined.',
  "notifications.message.registrationWaitlisted":
    'You\'ve been added to the waitlist for "{{eventTitle}}".',
  "notifications.message.teamAssigned": 'You\'ve been assigned to a team for "{{eventTitle}}".',
  "notifications.message.teamRemoved": 'You\'ve been removed from your team for "{{eventTitle}}".',
  "notifications.message.eventCancelled": 'The event "{{eventTitle}}" was cancelled.',
  "notifications.message.registrationSubmitted": 'A new volunteer registered for "{{eventTitle}}".',
  "notifications.message.registrationWithdrawn": 'A volunteer withdrew from "{{eventTitle}}".',
  "notifications.message.evaluationCompleted":
    'Your evaluation for "{{eventTitle}}" is ready to download.',
  "notifications.message.timesheetValidated":
    'Your logged hours for "{{eventTitle}}" were validated.',
  "notifications.message.timesheetRejected":
    'Your logged hours for "{{eventTitle}}" were rejected.',
  "notifications.message.manualHoursSubmitted":
    'A volunteer submitted manual hours for "{{eventTitle}}".',
  "notifications.message.orgRoleChanged": "Your role in {{orgName}} was changed to {{toRole}}.",
  "notifications.message.orgMemberRemoved": "You've been removed from {{orgName}}.",
  "notifications.message.shiftAssigned": 'You\'ve been assigned to a shift for "{{eventTitle}}".',
  "notifications.message.shiftUnassigned":
    'You\'ve been removed from a shift for "{{eventTitle}}".',
  "notifications.message.shiftUpdated":
    'A shift you were assigned to for "{{eventTitle}}" was updated; please check your shifts.',
  "notifications.message.shiftCancelled":
    'A shift you were assigned to for "{{eventTitle}}" was cancelled.'
};
